import type { Node } from 'web-tree-sitter';
import type { LanguageId } from '../pipeline/types';
import { getParserFor } from './tree-sitter';

export interface SymbolEntry {
  name: string;
  path: string;
  languageId: LanguageId;
  /** Declaration header only (up to the body), not the implementation. */
  signature: string;
  docstring?: string;
  startIndex: number;
  endIndex: number;
}

// Only module-level declarations are indexed — not nested class methods —
// since cross-file reference matching here is a plain identifier-name
// heuristic, not real scope resolution, and bare-name matching for methods
// (`method()` vs `Class.method()`) is too noisy to be useful.
const TOP_LEVEL_TYPES: Record<LanguageId, string[]> = {
  javascript: ['function_declaration', 'class_declaration', 'lexical_declaration', 'variable_declaration'],
  typescript: [
    'function_declaration',
    'class_declaration',
    'lexical_declaration',
    'variable_declaration',
    'interface_declaration',
    'type_alias_declaration',
  ],
  python: ['function_definition', 'class_definition'],
};

function unwrapExport(node: Node, languageId: LanguageId): Node {
  if (node.type !== 'export_statement') return node;
  const types = TOP_LEVEL_TYPES[languageId];
  return node.namedChildren.find((c) => types.includes(c.type)) ?? node;
}

function declarationName(node: Node): string | null {
  if (
    ['function_declaration', 'class_declaration', 'function_definition', 'class_definition', 'interface_declaration', 'type_alias_declaration'].includes(
      node.type,
    )
  ) {
    return node.childForFieldName('name')?.text ?? null;
  }
  if (node.type === 'lexical_declaration' || node.type === 'variable_declaration') {
    const declarator = node.namedChildren.find((c) => c.type === 'variable_declarator');
    return declarator?.childForFieldName('name')?.text ?? null;
  }
  return null;
}

function findDocstring(node: Node, outerNode: Node, sourceText: string, languageId: LanguageId): string | undefined {
  if (languageId === 'python') {
    const body = node.childForFieldName('body');
    const first = body?.namedChildren[0];
    if (first?.type === 'expression_statement') {
      const strNode = first.namedChildren[0];
      if (strNode?.type === 'string') return sourceText.slice(strNode.startIndex, strNode.endIndex);
    }
    return undefined;
  }
  const sibling = outerNode.previousSibling;
  if (sibling?.type === 'comment' && sibling.text.startsWith('/**')) return sibling.text;
  return undefined;
}

function signatureFor(node: Node, sourceText: string): string {
  const body = node.childForFieldName('body');
  if (body) {
    const header = sourceText.slice(node.startIndex, body.startIndex).trim();
    return header.endsWith(':') ? `${header} ...` : `${header} { ... }`;
  }
  return sourceText.slice(node.startIndex, node.endIndex).split('\n')[0];
}

export async function extractTopLevelSymbols(path: string, content: string, languageId: LanguageId): Promise<SymbolEntry[]> {
  const parser = await getParserFor(languageId);
  const tree = parser.parse(content);
  if (!tree) return [];

  const types = TOP_LEVEL_TYPES[languageId];
  const entries: SymbolEntry[] = [];

  for (const child of tree.rootNode.namedChildren) {
    const decl = unwrapExport(child, languageId);
    if (!types.includes(decl.type)) continue;
    const name = declarationName(decl);
    if (!name) continue;
    entries.push({
      name,
      path,
      languageId,
      signature: signatureFor(decl, content),
      docstring: findDocstring(decl, child, content, languageId),
      startIndex: child.startIndex,
      endIndex: child.endIndex,
    });
  }
  return entries;
}

// Module-level, in-memory project index. Rebuilt file-by-file as files load
// or change — see main.ts wiring (bulk index on load, debounced on edit).
const indexByFile = new Map<string, SymbolEntry[]>();

export async function updateSymbolIndexForFile(
  path: string,
  content: string,
  languageId: LanguageId | undefined,
): Promise<void> {
  if (!languageId) {
    indexByFile.delete(path);
    return;
  }
  try {
    indexByFile.set(path, await extractTopLevelSymbols(path, content, languageId));
  } catch {
    // Fail open: a parse error just means this file contributes nothing.
    indexByFile.delete(path);
  }
}

export function removeFileFromSymbolIndex(path: string): void {
  indexByFile.delete(path);
}

export function clearSymbolIndex(): void {
  indexByFile.clear();
}

export function allIndexedSymbols(): SymbolEntry[] {
  return Array.from(indexByFile.values()).flat();
}

export function findSymbol(name: string): SymbolEntry[] {
  return allIndexedSymbols().filter((s) => s.name === name);
}
